import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import "./shell.css";
import fire from "../config/Fire";
import { useAuth, roleHome } from "../auth/AuthProvider";
import { BookDataProvider, useBookData } from "../data/BookDataProvider";
import { STAFF_PAGES, REFERRAL_PAGES, ADMIN_PAGES, pagesForTier, shellHome } from "./navConfig";
import CommandPalette from "./CommandPalette";
import FawnBar from "../features/custodian/FawnBar";
import ErrorBoundary from "../components/ErrorBoundary";
import {
  Button,
  Menu,
  Skeleton,
  PlusIcon,
  SearchIcon,
  ChevronDownIcon,
  ExternalIcon,
  SwatchIcon,
  LogoutIcon,
  UsersIcon
} from "../ui";

/**
 * The persistent app shell (§2.2/§3.5): left sidebar + topbar wrapping every
 * /app/* route. Role-aware nav (Admin section only for admins), ⌘K palette,
 * "+ New" menu, user menu. Rendered inside RequireAuth, so the session is
 * already restored; role may still be resolving (skeleton nav meanwhile).
 */

function displayNameFor(identity, user) {
  if (identity) {
    const joined = [identity.firstName, identity.lastName].filter(Boolean).join(" ");
    if (identity.name) {
      return identity.name;
    }
    if (joined) {
      return joined;
    }
  }
  return (user && user.email) || "Account";
}

function initialsFor(name) {
  const parts = name
    .replace(/@.*/, "")
    .split(/[\s._-]+/)
    .filter(Boolean);
  const initials = parts
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");
  return initials || "?";
}

function AppShellInner() {
  const { user, role, roleReady, identity, tier } = useAuth();
  const { households } = useBookData();
  const navigate = useNavigate();
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Global ⌘K / Ctrl+K.
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const name = displayNameFor(identity, user);
  const legacyPath = roleHome(role);

  const paletteItems = useMemo(() => {
    const pages = pagesForTier(tier).map((page) => {
      const Icon = page.icon;
      return {
        id: page.path,
        label: page.label,
        hint: page.phase,
        icon: <Icon size={16} />,
        keywords: page.description,
        run: () => navigate(page.path)
      };
    });
    // The Phase-2 client index (§2.7 ⌘K): every household in the caller's
    // scoped book is reachable by name/email/phone/address. searchOnly items
    // appear once the user types, so the idle palette stays a short page list.
    const clients = households.map((h) => ({
      id: `client-${h.id}`,
      label: h.name,
      hint: "client",
      searchOnly: true,
      icon: <UsersIcon size={16} />,
      keywords: [
        h.client.email,
        h.client.telephone,
        h.client.address,
        h.client.firstName,
        h.client.lastName
      ]
        .filter(Boolean)
        .join(" "),
      run: () => navigate(`/app/clients/${h.id}`)
    }));
    // M-1: legacy/kit shortcuts follow the same tier gating as the sidebar.
    const internal = [];
    if (tier !== "referral") {
      internal.push({
        id: "legacy",
        label: "Open legacy dashboard",
        icon: <ExternalIcon size={16} />,
        keywords: "admin agent classic old",
        run: () => navigate(legacyPath)
      });
    }
    if (role === "admin" || process.env.NODE_ENV === "development") {
      internal.push({
        id: "kit",
        label: "Design kit",
        hint: "reference",
        icon: <SwatchIcon size={16} />,
        keywords: "components tokens gallery styleguide",
        run: () => navigate("/app/kit")
      });
    }
    return [
      ...pages,
      ...clients,
      ...internal,
      {
        id: "logout",
        label: "Log out",
        icon: <LogoutIcon size={16} />,
        run: async () => {
          await fire.auth().signOut();
          navigate("/login");
        }
      }
    ];
  }, [tier, role, legacyPath, navigate, households]);

  // The shell serves agents and admins. Clients get the read-only portal in a
  // later phase (§3.13); until then send them to their legacy dashboard, and
  // send uid-without-identity users to /login, which explains the situation.
  if (roleReady && role === "client") {
    return <Navigate to="/client" replace />;
  }
  if (roleReady && !role) {
    return <Navigate to="/login" replace />;
  }

  const newMenuItems = [
    { type: "label", label: "Create — opens the legacy form until Phase 3" },
    { label: "Quote", onSelect: () => navigate(legacyPath), hint: "legacy" },
    { label: "Prospect", onSelect: () => navigate(legacyPath), hint: "legacy" },
    { label: "Client", onSelect: () => navigate(legacyPath), hint: "legacy" },
    { label: "Policy", onSelect: () => navigate(legacyPath), hint: "legacy" },
    { label: "Service request", onSelect: () => navigate(legacyPath), hint: "legacy" }
  ];

  const userMenuItems = [
    { type: "label", label: name },
    // M-1: the legacy surface is staff-only; referral agents never get
    // pointed at it.
    ...(tier !== "referral"
      ? [
          {
            label: "Legacy dashboard",
            icon: <ExternalIcon size={14} />,
            onSelect: () => navigate(legacyPath)
          },
          { type: "separator" }
        ]
      : []),
    {
      label: "Log out",
      icon: <LogoutIcon size={14} />,
      danger: true,
      onSelect: async () => {
        await fire.auth().signOut();
        navigate("/login");
      }
    }
  ];

  return (
    <div className="shell">
      <aside className="shell-sidebar">
        <NavLink to={shellHome(tier)} className="shell-brand">
          <span className="shell-brand__mark">F</span>
          <span className="shell-brand__name">Fawn Online</span>
        </NavLink>

        <nav className="shell-nav" aria-label="Main">
          {!roleReady ? (
            <div style={{ padding: "var(--space-3)", display: "grid", gap: 12 }}>
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} height={14} style={{ opacity: 0.25 }} />
              ))}
            </div>
          ) : (
            <>
              {(tier === "referral" ? REFERRAL_PAGES : STAFF_PAGES).map((page) => {
                const Icon = page.icon;
                return (
                  <NavLink key={page.path} to={page.path} className="shell-nav__item">
                    <Icon size={16} />
                    <span>{page.label}</span>
                  </NavLink>
                );
              })}
              {role === "admin" ? (
                <>
                  <div className="shell-nav__section">Admin</div>
                  {ADMIN_PAGES.map((page) => {
                    const Icon = page.icon;
                    return (
                      <NavLink key={page.path} to={page.path} className="shell-nav__item">
                        <Icon size={16} />
                        <span>{page.label}</span>
                      </NavLink>
                    );
                  })}
                </>
              ) : null}
            </>
          )}
        </nav>

        {/* M-1 (E's QA): internal affordances stay off the referral rail —
            the design kit is a dev/admin reference, and the legacy dashboard
            is a staff surface (E: referral agents should never be sent
            there). Routes remain URL-reachable; this is nav hygiene. */}
        <div className="shell-nav__bottom">
          {role === "admin" || process.env.NODE_ENV === "development" ? (
            <NavLink to="/app/kit" className="shell-nav__item">
              <SwatchIcon size={16} />
              <span>Design kit</span>
            </NavLink>
          ) : null}
          {tier !== "referral" ? (
            <a
              className="shell-nav__item"
              href={legacyPath}
              onClick={(e) => {
                e.preventDefault();
                navigate(legacyPath);
              }}
            >
              <ExternalIcon size={16} />
              <span>Legacy dashboard</span>
            </a>
          ) : null}
        </div>
      </aside>

      <div className="shell-main">
        <header className="shell-topbar">
          <button
            type="button"
            className="shell-search"
            onClick={() => setPaletteOpen(true)}
            aria-label="Open command palette"
          >
            <SearchIcon size={14} />
            <span className="shell-search__label">Search…</span>
            <span className="shell-search__kbd">⌘K</span>
          </button>

          <span className="shell-topbar__spacer" />

          {tier === "referral" ? (
            /* E §0: for the referral tier THE action is referring — one
               persistent primary button, not a create menu. */
            <Button
              variant="primary"
              icon={<PlusIcon size={14} />}
              onClick={() => navigate("/app/refer")}
              aria-label="Refer a household"
            >
              <span className="shell-cta-label">Refer a household</span>
            </Button>
          ) : (
            <Menu
              align="right"
              items={newMenuItems}
              trigger={(open, toggle) => (
                <Button
                  variant="primary"
                  icon={<PlusIcon size={14} />}
                  onClick={toggle}
                  aria-expanded={open}
                  aria-label="New record"
                >
                  <span className="shell-cta-label">
                    New <ChevronDownIcon size={14} />
                  </span>
                </Button>
              )}
            />
          )}

          <Menu
            align="right"
            items={userMenuItems}
            trigger={(open, toggle) => (
              <button
                type="button"
                className="shell-avatar"
                onClick={toggle}
                aria-expanded={open}
                aria-label="Account menu"
              >
                {initialsFor(name)}
              </button>
            )}
          />
        </header>

        <main className="shell-content">
          <ErrorBoundary label="this page">
            <Outlet />
          </ErrorBoundary>
        </main>

        {/* "Fawn's Fix-its" (§10.7d) — docked to the content column, staff-only
            and behind FEATURES.fawnBar (self-gating; renders null otherwise). */}
        <FawnBar />
      </div>

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        items={paletteItems}
      />
    </div>
  );
}

/* The composed book store wraps the whole shell so Book/hub/Today and the
   ⌘K client index share ONE fetch of the scoped lists per session. */
function AppShell() {
  return (
    <BookDataProvider>
      <AppShellInner />
    </BookDataProvider>
  );
}

export default AppShell;
